import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, firstValueFrom, isObservable } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

export type SupportedLanguage = 'en' | 'nl' | 'es' | 'fr' | 'de' | 'hi';

@Injectable({
  providedIn: 'root'
})
export class LanguageService {
  private _translate: any = inject(TranslateService as any);
  private _language$ = new BehaviorSubject<SupportedLanguage>('en');

  constructor() {
    this._translate?.setDefaultLang?.('en');
    this._translate?.use?.('en');
  }

  instant(key: string, params?: Record<string, string>): string {
    return params ? (this._translate?.instant?.(key, params) ?? key) : (this._translate?.instant?.(key) ?? key);
  }

  setLanguage(lang: SupportedLanguage) {
    // Emit immediately so UI can react, then emit again once translations are loaded.
    this._language$.next(lang);
    const result = this._translate?.use?.(lang);
    try {
      if (isObservable(result)) {
        void firstValueFrom(result).finally(() => this._language$.next(lang));
      } else if (result && typeof result.then === 'function') {
        void result.finally(() => this._language$.next(lang));
      }
    } catch {
      // If anything goes wrong, still force a second emission as a best-effort refresh.
      this._language$.next(lang);
    }
  }

  get language$() {
    return this._language$.asObservable();
  }

  get currentLanguage(): SupportedLanguage {
    return this._language$.value;
  }
}
